import React from "react";
import UniversalWidget from "./universal/UniversalWidget";
import HeadToHeadWidget from "./head-to-head/HeadToHeadWidget";
import LiveTickerWidget from "./live-ticker/LiveTickerWidget";
import ResultsMapWidget from "./results-map/ResultsMapWidget";
import ResultsMapOverlayWidget from "./results-map/ResultsMapOverlayWidget";
import { fetchRaceData } from "./apis/events";
import { fetchFake } from "./apis/fake";
import { fetchWidgetConfig } from "./apis/widgets";
import "./base.scss";
import { sortAndMergeRaces, applyRaceSort } from "./helpers";

// How often we poll the API for data.
const widgetRefresh = process.env.REACT_APP_WIDGET_POLL_RATE * 1000;
const raceDataRefresh = process.env.REACT_APP_RESULTS_POLL_RATE * 1000;

// Map special widget-ids to corresponding fake.
const fakeMapping = {
  "head-to-head": "/data/elections-head-to-head.json",
  "universal": "/data/elections-universal.json",
  "qa-scenarios-head-to-head": "/data/qa-scenarios-head-to-head.json",
  "qa-scenarios-universal": "/data/qa-scenarios-universal.json",
};

/**
 * Application root.
 *
 * This component fetches the necessary data and owns the feed state. It will
 * render the appropriate component UniversalWidget or HeadToHeadWidget based
 * on the type in the widget feed.
 *
 * It expects two properties: "eventId" and "widgetId". If `eventId` is the
 * value "fake" and `widgetId` is a key in `fakeMapping`, the corresponding
 * fake feed data from ../public/data/ will be fetched and used. Otherwise, the
 * value is treated as the widget ID and used to fetch from the elections API.
 */
class App extends React.Component {
  state = { widget: {}, results: [], hasWidgetError: false, hasRaceError: false, hasLoaded: false, updatedDate: "" };

  async componentDidMount() {
    // If pre-rendered data exists (SSR HTML page), use it for initial render
    if (window.__ELECTION_DATA__) {
      const { widget, races } = window.__ELECTION_DATA__;
      let results = races;
      if (widget.raceMetadata) {
        const useRacesForCandidateList = widget.widgetType === "universal" || widget.widgetType === "results-map" || widget.widgetType === "results-map-overlay" || widget.widgetType === "live-ticker";
        results = sortAndMergeRaces(widget, results, useRacesForCandidateList);
      }
      // Same race ordering as the polling path so first paint and refreshes agree.
      results = applyRaceSort(widget, this.props.widgetId, results, process.env.REACT_APP_ALPHABETICAL_SORT_WIDGET_ID);
      this.setState({ widget, results, hasLoaded: true, updatedDate: new Date() });
      delete window.__ELECTION_DATA__;
    } else {
      // Normal flow: fetch from API
      await this.getWidget(this.props.eventId, this.props.widgetId);
      await this.getRaceData(this.props.eventId, this.props.widgetId);
    }

    // Start polling for live updates
    this.widgetInterval = setInterval(
      () => this.getWidget(this.props.eventId, this.props.widgetId),
      widgetRefresh
    );

    this.raceDataInterval = setInterval(
      () => this.getRaceData(this.props.eventId, this.props.widgetId),
      raceDataRefresh
    );
  };

  componentWillUnmount() {
    clearInterval(this.widgetInterval);
    clearInterval(this.raceDataInterval);
  }

  async getWidget(eventId, widgetId) {
    let widget;

    try {
      if (eventId === "fake" && widgetId in fakeMapping) {
        widget = await fetchFake(fakeMapping[widgetId]);
      } else {
        widget = await fetchWidgetConfig(eventId, widgetId);
      }

      this.setState({ hasWidgetError: false, hasLoaded: true, widget });

      // Conditionally add GTM scripts if not already added
      if (widget && widget.accountId === 'cmg-ajc' && !document.getElementById('gtm-script')) {
          this.addGtmScripts();
      }
    } catch (err) {
      console.error("Error fetching widget configuration", err);
      this.setState({ hasWidgetError: true });
    }
  }

  addGtmScripts() {
    // Add GTM script to head if not already present
    const gtmScript = document.createElement('script');
    gtmScript.id = 'gtm-script';
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-WT4CBT7')`;
    document.head.appendChild(gtmScript);

    // Add GTM noscript to body if not already present
    const gtmNoScript = document.createElement('noscript');
    gtmNoScript.id = 'gtm-noscript';
    gtmNoScript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WT4CBT7"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;
    document.body.appendChild(gtmNoScript);
  }

  async getRaceData(eventId, widgetId) {
    let results;

    try {
      if (eventId === "fake" && widgetId in fakeMapping) {
        results = (await fetchFake(fakeMapping[widgetId])).races;
      } else {
        results = await fetchRaceData(eventId, widgetId);
      }

      const useRacesForCandidateList = this.state.widget.widgetType === "universal" || this.state.widget.widgetType === "results-map" || this.state.widget.widgetType === "results-map-overlay" || this.state.widget.widgetType === "live-ticker";

      // Sort races by the raceMetadata ordering in widget config and merge in
      // the widget candidateMetadata. If there is no raceMetadata on the widget
      // config, just use the races as they are.
      results = sortAndMergeRaces(this.state.widget, results, useRacesForCandidateList);

      // Per-widget "Race Order" (widget.raceSort), plus the legacy hardcoded
      // alphabetical widget id. No-op for default/unset.
      results = applyRaceSort(this.state.widget, this.props.widgetId, results, process.env.REACT_APP_ALPHABETICAL_SORT_WIDGET_ID);

      this.setState({ hasRaceError: false, results, updatedDate: new Date() });
    } catch (err) {
      console.error("Error fetching race results", err);
      this.setState({ hasRaceError: true });
    }
  }

  renderWidget() {
    if (this.state.hasLoaded) {
      switch (this.state.widget.widgetType) {
        case "head-to-head":
          return <HeadToHeadWidget widget={this.state.widget} results={this.state.results} updated={this.state.updatedDate} />
        case "universal":
          return <UniversalWidget widget={this.state.widget} races={this.state.results} updated={this.state.updatedDate} />
        case "live-ticker":
          return <LiveTickerWidget widget={this.state.widget} races={this.state.results} updated={this.state.updatedDate} />
        case "results-map":
          return <ResultsMapWidget widget={this.state.widget} races={this.state.results} updated={this.state.updatedDate} />
        case "results-map-overlay":
          return <ResultsMapOverlayWidget widget={this.state.widget} races={this.state.results} updated={this.state.updatedDate} />
        default:
          console.warn(`[election-center-widget] Unknown widget type: ${this.state.widget.widgetType}`);
          return this.renderError();
      }
    } else {
      return (<></>);
    }
  }

  renderError() {
    return (
      <div className="ec-widgets-error">
        {process.env.REACT_APP_ERROR_MESSAGE}
      </div>
    );
  }

  render() {
    const hasError = this.state.hasWidgetError || this.state.hasRaceError;

    return (
      <div className="container">
        {hasError ? this.renderError() : this.renderWidget()}
      </div>
    );
  }
}

export default App;
